import {
    cloneElement,
    FC,
    ReactElement,
    ReactNode,
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
} from 'react';
import { SwitchTransition } from 'react-transition-group';
import { styled } from 'styled-components';
import { useIsMobile } from '../../utils/getIsMobile';
import { getWindow } from '../../utils/getWindow';
import { IconClose } from '../icon/IconClose';
import { IconButton } from '../input/IconButton';
import { Box, BoxProps } from '../layout/Box';
import { Flex } from '../layout/Flex';
import { TransitionSlideUp } from '../transition/TransitionSlideUp';
import { Backdrop } from './Backdrop';
import { DrawerContainer, DrawerContent, DrawerTitle } from './Drawer';

interface SmartDrawerProps extends BoxProps {
    onClose: VoidFunction;
    step?: string;
    titleContent?: ((closeHandler: VoidFunction) => ReactNode) | ReactNode;
    mainContent?: ((closeHandler: VoidFunction) => ReactNode) | ReactNode;
    titleStyles?: BoxProps;
    mainStyles?: BoxProps;
    timeout?: number;
    defaultCloseButtonEnabled?: boolean;
    closeButtonSlot?: ReactElement;
    hideTrigger?: boolean;
    forceCloseButton?: boolean;
}

export const SwipeContainer = styled(Flex)
    .withConfig({ displayName: 'SwipeContainer' })
    .attrs((props) => ({
        width: '100%',
        alignItems: 'center',
        justifyContent: 'end',
        maxWidth: '580px',
        ...props,
    }))``;

const SwipeTrigger = styled(Box)
    .withConfig({ displayName: 'SwipeTrigger' })
    .attrs((props) => ({
        width: 34,
        height: 5,
        position: 'absolute',
        top: 1,
        left: '50%',
        background: 'var(--divider)',
        zIndex: 1202,
        transform: 'translateX(-50%)',
        borderRadius: 1,
        ...props,
    }))``;

const SLIDE_TO_CLOSE_LIMIT = 55;

/**
 * SmartDrawer - Drower, который умеет закрываться свайпом на мобилке и автоматически открывается и закрывается с анимацией.
 * В десктопной версии имеет кнопку закрытия, в мобильной имеет визуальный триггер закрытия.
 * Если вы хотите, чтобы между разными шагами вашего дровера была доп анимация - используйте пропс step
 *
 * @param titleContent - контент заголовка. Или JSX, или функция, принимающая функцию закрытия дровера с анимацией в качестве аргумента и возвращающая JSX
 * @param mainContent - основной контент. Или JSX, или функция, принимающая функцию закрытия дровера с анимацией в качестве аргумента и возвращающая JSX
 * @param onClose - функция закрытия дровера
 * @param step - текущий шаг дровера, передавайте только в том случае, если вы хотите, чтобы между разными шагами вашего дровера была доп анимация
 * @param titleStyles - стили заголовка
 * @param mainStyles - стили основного контента
 * @param timeout - время анимации закрытия дровера
 * @param defaultCloseButtonEnabled - флаг, который определяет, должна ли быть кнопка закрытия дровера
 * @param closeButtonSlot - слот для кнопки закрытия дровера
 * @param hideTrigger - флаг, который определяет, должен ли быть визуальный триггер закрытия дровера
 * @param forceCloseButton - показать кнопку закрытия дровера даже на тачах
 * @param other - остальные пропсы дровера (копирует пропсы Box)
 */
export const SmartDrawer: FC<SmartDrawerProps> = ({
    titleContent,
    mainContent,
    onClose,
    closeButtonSlot,
    step,
    titleStyles,
    mainStyles,
    timeout = 300,
    defaultCloseButtonEnabled = true,
    hideTrigger,
    forceCloseButton,
    ...other
}) => {
    const [swipeContainer, setSwipeContainer] = useState<HTMLDivElement | null>(null);
    const [swipeScroller, setSwipeScroller] = useState<HTMLDivElement | null>(null);
    const [localStep, setLocalStep] = useState<'OPEN' | 'CLOSE'>('OPEN');
    const [currentStep, setCurrentStep] = useState<string>(step || localStep);
    const prevStep = useRef(currentStep);
    const [titleContentWithAnimationDelay, setTitleContentWithAnimationDelay] = useState<
        SmartDrawerProps['titleContent'] | null
    >(null);
    const [mainContentWithAnimationDelay, setMainContentWithAnimationDelay] = useState<
        SmartDrawerProps['mainContent'] | null
    >(null);
    const [mainStylesWithAnimationDelay, setMainStylesWithAnimationDelay] = useState<
        SmartDrawerProps['mainStyles'] | null
    >(null);
    const [titleStylesWithAnimationDelay, setTitleStylesWithAnimationDelay] = useState<
        SmartDrawerProps['titleStyles'] | null
    >(null);

    const isFirstRenderRef = useRef(true);

    const isMobile = useIsMobile();

    const handleClose = useCallback(() => {
        setLocalStep('CLOSE');
        setTimeout(onClose, timeout);
    }, [onClose, timeout]);

    useEffect(() => {
        step && setCurrentStep(step);
    }, [step]);

    useEffect(() => {
        setCurrentStep(localStep);
    }, [localStep]);

    useEffect(() => {
        getWindow()?.document.body.style.setProperty('overflow', 'hidden');

        return () => {
            getWindow()?.document.body.style.removeProperty('overflow');
        };
    }, []);

    useLayoutEffect(() => {
        if (!swipeContainer || !swipeScroller) {
            return;
        }

        let detectEvalible = false;
        let startY = 0;
        let delta = 0;

        const onTouchStart = (e: TouchEvent) => {
            if (swipeScroller.scrollTop === 0) {
                detectEvalible = true;
                startY = e.touches[0].clientY;
            }
        };

        const onTouchEnd = () => {
            if (delta > SLIDE_TO_CLOSE_LIMIT) {
                handleClose();
                detectEvalible = false;

                return;
            }

            swipeScroller.style.removeProperty('overflow');
            detectEvalible = false;
        };

        const onTouchMove = (e: TouchEvent) => {
            if (!detectEvalible) {
                return;
            }

            const currentY = e.touches[0].clientY;

            delta = currentY - startY;

            if (delta < 0) {
                return;
            }

            swipeScroller.style.setProperty('overflow', 'hidden');
            swipeContainer.style.transform = `translateY(${delta}px)`;
        };

        swipeContainer.addEventListener('touchstart', onTouchStart);
        swipeContainer.addEventListener('touchmove', onTouchMove);
        swipeContainer.addEventListener('touchend', onTouchEnd);

        return () => {
            swipeContainer.removeEventListener('touchstart', onTouchStart);
            swipeContainer.removeEventListener('touchmove', onTouchMove);
            swipeContainer.removeEventListener('touchend', onTouchEnd);
        };
    }, [handleClose, swipeContainer, swipeScroller]);

    useEffect(() => {
        prevStep.current = currentStep;

        if (isFirstRenderRef.current || currentStep === prevStep.current) {
            isFirstRenderRef.current = false;

            setTitleContentWithAnimationDelay(() => titleContent);
            setMainContentWithAnimationDelay(() => mainContent);
            setTitleStylesWithAnimationDelay(titleStyles);
            setMainStylesWithAnimationDelay(mainStyles);

            return;
        }

        const timerId = getWindow()?.setTimeout(() => {
            setTitleContentWithAnimationDelay(() => titleContent);
            setMainContentWithAnimationDelay(() => mainContent);
            setTitleStylesWithAnimationDelay(titleStyles);
            setMainStylesWithAnimationDelay(mainStyles);
        }, timeout);

        return () => {
            getWindow()?.clearTimeout(timerId);
        };
    }, [titleContent, titleStyles, mainContent, mainStyles, timeout, currentStep]);

    const isCloseButtonEnabled = (defaultCloseButtonEnabled && !isMobile) || forceCloseButton;

    return (
        <Backdrop onClose={handleClose} justifyContent={isMobile ? 'end' : 'center'}>
            <SwitchTransition>
                <TransitionSlideUp key={currentStep} timeout={timeout}>
                    <SwipeContainer id="swipe-container" ref={(node) => setSwipeContainer(node)}>
                        {isMobile && !hideTrigger ? <SwipeTrigger id="swipe-trigger" /> : null}
                        <DrawerContainer isSmartDrawer {...other}>
                            {titleContent ? (
                                <DrawerTitle
                                    direction="row"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    p={2}
                                    {...titleStylesWithAnimationDelay}
                                >
                                    {titleContentWithAnimationDelay &&
                                    typeof titleContentWithAnimationDelay === 'function'
                                        ? titleContentWithAnimationDelay(handleClose)
                                        : titleContentWithAnimationDelay}
                                    {isCloseButtonEnabled ? (
                                        <Box>
                                            <IconButton onClick={handleClose}>
                                                <IconClose />
                                            </IconButton>
                                        </Box>
                                    ) : null}
                                </DrawerTitle>
                            ) : null}

                            {isCloseButtonEnabled && !titleContent ? (
                                <DrawerTitle
                                    direction="row"
                                    justifyContent="end"
                                    alignItems="center"
                                    p={2}
                                >
                                    <Box>
                                        <IconButton onClick={handleClose}>
                                            <IconClose />
                                        </IconButton>
                                    </Box>
                                </DrawerTitle>
                            ) : null}

                            {!titleContent && !isCloseButtonEnabled && closeButtonSlot
                                ? cloneElement(closeButtonSlot, { onClick: handleClose })
                                : null}

                            {mainContent ? (
                                <DrawerContent
                                    ref={(node) => setSwipeScroller(node)}
                                    p={2}
                                    pt={0}
                                    {...mainStylesWithAnimationDelay}
                                >
                                    {mainContentWithAnimationDelay &&
                                    typeof mainContentWithAnimationDelay === 'function'
                                        ? mainContentWithAnimationDelay(handleClose)
                                        : mainContentWithAnimationDelay}
                                </DrawerContent>
                            ) : null}
                        </DrawerContainer>
                    </SwipeContainer>
                </TransitionSlideUp>
            </SwitchTransition>
        </Backdrop>
    );
};
